import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCopilotConversation,
  getCurrentCopilotConversation,
} from "@/features/copilot/api/copilot.api";
import { copilotConversationPollInterval } from "@/features/copilot/lib/copilot-polling";
import { type CopilotScopeKey, copilotQueryKeys } from "@/features/copilot/lib/copilot-query-keys";

const inFlightCreations = new Map<string, ReturnType<typeof createCopilotConversation>>();

function createOnce(scope: CopilotScopeKey) {
  const key = JSON.stringify(copilotQueryKeys.conversation(scope));
  const active = inFlightCreations.get(key);
  if (active) return active;
  const pending = createCopilotConversation(scope.workspaceId).finally(() =>
    inFlightCreations.delete(key),
  );
  inFlightCreations.set(key, pending);
  return pending;
}

export function useCopilotConversation(
  scope: CopilotScopeKey | null,
  localRunConversationId: string | null = null,
) {
  const queryClient = useQueryClient();
  const queryKey = scope ? copilotQueryKeys.conversation(scope) : copilotQueryKeys.all;
  const current = useQuery({
    queryKey,
    queryFn: () => {
      if (!scope) throw new Error("A verified copilot scope is required.");
      return getCurrentCopilotConversation(scope.workspaceId);
    },
    // The current-conversation route is not on shore yet. Leaving this query
    // on asked every signed-in page for a 404.
    enabled: false && scope !== null,
    retry: false,
    staleTime: 0,
    refetchInterval: (query) =>
      copilotConversationPollInterval(
        localRunConversationId,
        query.state.data?.id,
        query.state.data?.activeTurn?.status,
        query.state.data?.actions,
      ),
    refetchIntervalInBackground: false,
  });
  const create = useMutation({
    mutationKey: [
      ...copilotQueryKeys.all,
      "create",
      scope?.accountId ?? null,
      scope?.userId ?? null,
      scope?.workspaceId ?? null,
    ],
    mutationFn: (scopeAtInvocation: CopilotScopeKey) => createOnce(scopeAtInvocation),
    onSuccess: (conversation, scopeAtInvocation) =>
      queryClient.setQueryData(copilotQueryKeys.conversation(scopeAtInvocation), conversation),
  });
  return { current, create, queryKey };
}
